import { ref } from "vue";
import { useUser } from "@domain/Security/User";
import type { IJupiterAppProps, IJupiterProps } from ".";
import { useAuthenticationApi } from "./Types/AuthenticationApi";
import { useAuth } from "@library/Security";
import { Deferred } from "@library/Utilities/Deferred";
import { isDevelopment } from "@utilities/Environments";

// These props will always be environment specific
import { mockProps } from "@jupiter-props";
import { setAppConfiguration } from "@domain/AppConfiguration";
import { useTheme } from "@services/Theme";
import type { IAuthenticationResponse } from "./Types/IAuthenticationResponse";
import { useRouter } from "@library/Services";

/**
 * Retrieves a static instance of the jupiter props.  This is static because we wont have the query string on every
 * page navigation, so we need to maintain the props state between routes.
 * @returns
 */
export function useJupiterProps()
{
    return JupiterProps.instance.value;
}

/**
 * A class that holds the properties sent into the WebView from the Jupiter App
 */
class JupiterProps implements IJupiterProps
{

    static instance = ref( new JupiterProps() );

    // App props
    appMode?: 'dark' | 'light';
    gpsLat?: number;
    gpsLong?: number;

    // Auth props
    appConsumerId?: string;
    appVehicleId?: string;
    nonce?: number;
    timestamp?: number;
    sign?: string;

    // Carbucks props
    isDarkMode?: boolean;
    isAuthenticated: boolean = false;

    // Awaitable ready state deferred promise
    deferredIsReady = new Deferred<boolean>();

    /**
     * Updates the instance with the query values
     * @param query
     */
    async initialise( forceRefresh: boolean = false )
    {

        const user = useUser();
        const query = Object.fromEntries( new URLSearchParams( location.search ) ) as IJupiterAppProps;
        const isAuthenticated = user != null;
        const hasVehicleChanged = ( query?.appVehicleId && user?.activeVehicle?.appVehicleId != query?.appVehicleId ) ?? true;

        // In development, prefer the values in the query string over the mock props (for testing)
        if( isDevelopment() )
        {
            const props = mockProps as IJupiterAppProps;
            this.appVehicleId = query?.appVehicleId ?? props.appVehicleId;
            this.appConsumerId = query?.appConsumerId ?? props.appConsumerId;
            this.appMode = query?.appMode ?? props.appMode ?? 'dark'; // App Mode is needed for when we update the query string parameters.
            this.gpsLat = parseFloat( String( query?.gpsLat ?? props.gpsLat ) );
            this.gpsLong = parseFloat( String( query?.gpsLong ?? props.gpsLong ) );
            this.nonce = query?.nonce ?? mockProps.nonce;
            this.timestamp = query?.timestamp ?? mockProps.timestamp;
            this.sign = query?.sign ?? mockProps.sign;
        }

        // Everywhere else, prefer the values that have been overridden by the mock props
        else
        {
            const props = mockProps as IJupiterAppProps;
            this.appVehicleId = props.appVehicleId ?? query?.appVehicleId;
            this.appConsumerId = props.appConsumerId ?? query?.appConsumerId;
            this.appMode = props.appMode ?? query?.appMode ?? 'dark'; // App Mode is needed for when we update the query string parameters.
            this.gpsLat = parseFloat( String( props.gpsLat ?? query?.gpsLat  ) );
            this.gpsLong = parseFloat( String(  props.gpsLong ?? query?.gpsLong ) );
            this.nonce = mockProps.nonce ?? query?.nonce;
            this.timestamp = mockProps.timestamp ?? query?.timestamp;
            this.sign = mockProps.sign ?? query?.sign;
        }

        this.isAuthenticated = isAuthenticated && hasVehicleChanged == false && forceRefresh == false;
        this.isDarkMode = this.getDarkMode( query );

        // We need to ensure the user is authenticated
        await this.authenticate();

        this.deferredIsReady.resolve( true );

    }

    /**
     * Awaitable function that resolves once the Jupiter props are loaded
     * and authentication has been attempted
     * @returns
     */
    async isReady()
    {
        return await this.deferredIsReady;
    }

    /**
     * True if the deferred isReady promise has not yet been resolved
     */
    get isInitialising()
    {
        return this.deferredIsReady.isFinalised == false;
    }

    /**
     * Gets the query object that can be used to update the route's querystring
     * @returns
     */
    getQuery()
    {
        return {
            appVehicleId: this.appVehicleId,
            appConsumerId: this.appConsumerId,
            appMode: this.appMode || "dark",
            gpsLat: this.gpsLat,
            gpsLong: this.gpsLong,
            nonce: this.nonce,
            timestamp: this.timestamp,
            sign: this.sign,
        };
    }

    /**
     * Gets the authentication parameters
     */
    getAuthenticationParams(): IJupiterAppProps
    {
        return {
            appConsumerId: this.appConsumerId,
            appVehicleId: this.appVehicleId,
            appMode: this.appMode,
            gpsLat: this.gpsLat ?? 0,
            gpsLong: this.gpsLong ?? 0,
            nonce: this.nonce,
            timestamp: this.timestamp,
            sign: this.sign
        };
    }

    /**
     * Gets the dark mode state from the parameters passed in the query string
     * @param query
     * @returns
     */
    private getDarkMode( query: any )
    {
        if( String( query?.appMode ?? '' ).toLowerCase() == "light" ) return false;
        return true;
    }

    /**
     * Tries to authenticate the user against the DB.
     * @param params
     * @returns
     */
    private async tryToAuthenticateUser( params: IJupiterAppProps ): Promise<IAuthenticationResponse|null>
    {
        const api = useAuthenticationApi();

        // Delay between 0 and 250 ms to add some jitter to help with Consumer key exception issue
        const delay = Math.random() * 250;
        await new Promise( resolve => setTimeout( resolve, delay ) );

        const response = await api.authenticateAsync( params );

        if( response == null ) return null;
        if( response.user == null ) return null;

        // If the response was successful, save the active vehicle to local storage
        else if( response.vehicle != null )
        {
            localStorage.setItem( 'activeVehicle', JSON.stringify( response.vehicle ) );
        }

        return response;
    }

    /**
     * Authenticate the user using the jupiter properties
     * @returns
     * @param force Set to true to force re-authentication if the user is already authenticated
     */
    async authenticate( force: boolean = false )
    {

        //if( this.isAuthenticated && force == false ) return;

        const params = this.getAuthenticationParams();
        const response = await this.tryToAuthenticateUser( params );

        if( response == null )
        {
            console.log( '- FAILED TO AUTHENTICATE' );
            this.isAuthenticated = false;
            return;
        }

        // Set the user and auth tokens now, because the appConfiguration initialisation may have to
        // hit the API to retrieve offers.
        const auth = useAuth();
        auth.setUserAndTokens( response );

        // Set the app configuration.  If the property is null, the default app configuration will be used
        const appConfiguration = setAppConfiguration( response.appConfiguration );
        if( appConfiguration.isGeneric == false )
        {
            await appConfiguration.initialise();
        }

        // The app configuration is not valid, we need to logout and exit,
        // which includes removing the user and tokens
        if( appConfiguration.isValid == false )
        {
            localStorage.removeItem( 'user');
            localStorage.removeItem( 'tokens');
            this.isAuthenticated = false;
            return;
        }

        // Set the theme from the app configuration.
        const theme = useTheme();
        theme.setTheme( appConfiguration.theme );

        // Mark the Jupiter props as authenticated
        this.isAuthenticated = true;

        const router = useRouter();

        if( response.user.isDealerMode )
        {
            router.go( '/dealer-mode' );
        }
        else if ( router.current.value.path == '/dealer-mode' )
        {
            router.go( '/my-garage' );
        }
    }

}
